import type { Attachment, ChatConfig, ResolvedChatConfig, HeaderProvider } from './types';

export function uid(prefix = 'm'): string {
  const rand =
    typeof crypto !== 'undefined' && 'randomUUID' in crypto
      ? crypto.randomUUID().slice(0, 8)
      : Math.random().toString(36).slice(2, 10);
  return `${prefix}_${Date.now().toString(36)}_${rand}`;
}

export function cx(...parts: Array<string | false | null | undefined>): string {
  return parts.filter(Boolean).join(' ');
}

export function formatBytes(bytes: number): string {
  if (!Number.isFinite(bytes) || bytes <= 0) return '0 B';
  const units = ['B', 'KB', 'MB', 'GB'];
  const i = Math.min(Math.floor(Math.log(bytes) / Math.log(1024)), units.length - 1);
  const value = bytes / 1024 ** i;
  return `${value >= 10 || i === 0 ? Math.round(value) : value.toFixed(1)} ${units[i]}`;
}

export function formatTime(ts: number): string {
  try {
    return new Date(ts).toLocaleTimeString(undefined, { hour: '2-digit', minute: '2-digit' });
  } catch {
    return '';
  }
}

export function resolveConfig(config: ChatConfig): ResolvedChatConfig {
  return {
    apiKeyHeader: 'Authorization',
    apiKeyPrefix: 'Bearer ',
    protocol: 'sse',
    historyLimit: 20,
    timeoutMs: 120_000,
    attachmentMode: 'multipart',
    payloadField: 'payload',
    fileField: 'files',
    ...config,
  };
}

export async function resolveHeaders(
  config: ResolvedChatConfig,
  extra?: Record<string, string>,
): Promise<Record<string, string>> {
  const provided: HeaderProvider | undefined = config.headers;
  const dynamic = typeof provided === 'function' ? await provided() : provided;
  const headers: Record<string, string> = { ...dynamic, ...extra };
  if (config.apiKey) {
    headers[config.apiKeyHeader] = `${config.apiKeyPrefix}${config.apiKey}`;
  }
  return headers;
}

/** Strips `File` handles and base64 blobs so history stays serialisable and small. */
export function serialiseAttachment(a: Attachment) {
  return {
    id: a.id,
    name: a.name,
    size: a.size,
    mimeType: a.mimeType,
    ...(a.remoteId ? { remoteId: a.remoteId } : {}),
    ...(a.url ? { url: a.url } : {}),
    ...(a.data ? { data: a.data } : {}),
  };
}

export function fileToBase64(file: File): Promise<string> {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onerror = () => reject(reader.error ?? new Error('Failed to read file'));
    reader.onload = () => {
      const result = String(reader.result ?? '');
      const comma = result.indexOf(',');
      resolve(comma >= 0 ? result.slice(comma + 1) : result);
    };
    reader.readAsDataURL(file);
  });
}

/** Matches a file against an `accept`-style list ("image/*,.pdf,text/plain"). */
export function matchesAccept(file: File, accept?: string): boolean {
  if (!accept) return true;
  const patterns = accept
    .split(',')
    .map((p) => p.trim().toLowerCase())
    .filter(Boolean);
  if (patterns.length === 0) return true;
  const type = (file.type || '').toLowerCase();
  const name = file.name.toLowerCase();
  return patterns.some((p) => {
    if (p.startsWith('.')) return name.endsWith(p);
    if (p.endsWith('/*')) return type.startsWith(p.slice(0, -1));
    return type === p;
  });
}

export function isAbortError(err: unknown): boolean {
  return (
    err instanceof DOMException
      ? err.name === 'AbortError'
      : typeof err === 'object' && err !== null && (err as { name?: string }).name === 'AbortError'
  );
}

export function toError(err: unknown, fallback: string): Error {
  if (err instanceof Error) return err;
  if (typeof err === 'string') return new Error(err);
  return new Error(fallback);
}

/** Safe localStorage access: private mode / disabled storage must not crash the widget. */
export const storage = {
  get<T>(key: string): T | null {
    try {
      const raw = window.localStorage.getItem(key);
      return raw ? (JSON.parse(raw) as T) : null;
    } catch {
      return null;
    }
  },
  set(key: string, value: unknown): void {
    try {
      window.localStorage.setItem(key, JSON.stringify(value));
    } catch {
      /* quota exceeded or storage disabled */
    }
  },
  remove(key: string): void {
    try {
      window.localStorage.removeItem(key);
    } catch {
      /* ignore */
    }
  },
};

export function clamp(value: number, min: number, max: number): number {
  return Math.min(Math.max(value, min), max);
}
